import { FormState } from '../states/kyc-form-state';
import * as OptionController from '../controllers/OptionsController';
import Store from '../stores/Store';
import { signal } from '@preact/signals';
import { getDocument } from '../routes/kyc/utils';
import { GlobalState } from '../states/global-state';
import { route } from 'preact-router';
import { completenessPercentage } from '../controllers/KycController';
import {
  postLoginFATCA,
  postLoginKYC,
  postLoginRiskProfile,
} from '../constants/modal-messages';
import { BASE_URL } from '../constants/api';
import { moment } from '../constants/lib';

type getOriginValueArgsType = {
  optionCategory: string;
  optionGetter: string;
  selected: string;
  label: string;
};

export function getDeviceType() {
  if (typeof window !== 'undefined') {
    if (navigator.userAgent.includes('Android')) {
      return 'android';
    } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      return 'ios';
    } else {
      return 'desktop';
    }
  }
}

export function toggler(target, activeClass, parentSelector) {
  const parent = target.closest(parentSelector);
  if (parent) {
    const active = parent.querySelector(`.${activeClass}`);
    if (active) {
      active.classList.remove(activeClass);
    }
    target.classList.add(activeClass);
  }
}

export function togglerPersist(store, datasets, comparison, indicator) {
  const current = store();
  const cloned = [...datasets];
  if (current) {
    const index = cloned.findIndex(item => item[comparison] == current);
    if (index == -1) {
      store(cloned[0][comparison]);
      cloned[0][indicator] = true;
    } else {
      cloned[index][indicator] = true;
    }
  } else {
    store(cloned[0][comparison]);
    cloned[0][indicator] = true;
  }
  return cloned;
}

export function pause(timeout) {
  return new Promise(res => {
    if (timeout) {
      setTimeout(res, timeout || 0);
    }
  });
}

export function getFormDataLimit(controls) {
  return function getFormData<T>(form: HTMLFormElement): T {
    const keys = [...new Set(walkControls(controls).map(item => item.name))];

    const formData = new FormData(form);
    const data = new Map();

    for (const [key, value] of formData.entries()) {
      if (keys.includes(key)) {
        if (value == 'false') {
          data.set(key, false);
        } else if (value == 'true') {
          data.set(key, true);
        } else if (typeof value == 'string') {
          const sanitized = decodeURIComponent(value.replace(/<.*>/, ''));

          !['', ' '].includes(value) && data.set(key, sanitized);
        } else {
          //type file
          data.set(key, value);
        }
      }
    }

    const d = {};

    data.forEach((value, key) => {
      d[key] = value;
    });

    return { ...d } as T;
  };
}

export function walkControls(controls) {
  return controls.reduce((accu, item) => {
    const { controls } = item;
    if (controls) {
      for (const key in controls) {
        if (Object.prototype.hasOwnProperty.call(controls, key)) {
          accu.push(controls[key]);
        }
      }
    } else {
      accu.push(item);
    }
    return accu;
  }, []);
}

export async function showAlert(config: {
  text?: string;
  html?: string;
  icon: string;
  title: string;
  allowOutsideClick?: boolean;
  showConfirmButton?: boolean;
}) {
  const swal = window['swal'];
  if (swal) {
    return swal.fire(config);
  }
}

export async function showAutoCloseAlert(config: {
  icon: string;
  title: string;
  html: string;
  timer?: number;
  timerProgressBar?: boolean;
}) {
  // console.trace();
  // alert('pause');
  const Swal = window['swal'];
  if (Swal) {
    let timerInterval;
    return new Promise<void>(res => {
      Swal.fire({
        title: config.title,
        html: config.html,
        timer: config.timer || 2000,
        timerProgressBar: config.timerProgressBar || false,
        allowOutsideClick: false,
        showConfirmButton: false,
        icon: config.icon,
        didOpen: () => {
          Swal.showLoading();
          const b = Swal.getHtmlContainer().querySelector('b');
          timerInterval = setInterval(() => {
            b.textContent = Swal.getTimerLeft();
          }, 100);
        },
        willClose: () => {
          clearInterval(timerInterval);
        },
      }).then(result => {
        /* Read more about handling dismissals below */
        if (result.dismiss === Swal.DismissReason.timer) {
          // console.log('I was closed by the timer');
          res();
        }
      });
    });
  }
}

export function getDisplay(
  options,
  value,
  compareKey?,
  compareDisplay?,
  type?
) {
  const row = options.filter(item => {
    let test = false;
    if (type == 'riskprofile') {
      let compareValue = item[compareKey];
      if (compareValue && compareValue.includes('_')) {
        const [questionId, answerId, score] = compareValue.split('_');
        compareValue = answerId;
      }

      if (value && value.constructor.name == 'Array') {
        // console.log(174, item, value, compareValue);
        test = value.includes(Number(compareValue));
      } else {
        test = compareValue == value;
      }
    } else {
      test = item[compareKey || 'code'] == value;
    }

    return test;
  });

  if (row && row.length) {
    const matched = row.reduce((accu, iter) => {
      accu.push(iter[compareDisplay || 'value']);
      return accu;
    }, []);

    return matched.length <= 1 ? matched[0] : matched;
  }
  return '';
}

export function getOriginValue(config: {
  country: getOriginValueArgsType;
  province: getOriginValueArgsType;
  city: getOriginValueArgsType;
}) {
  const body = [];
  return OptionController[config.country.optionGetter]({
    category: config.country.optionCategory,
  })
    .then(res => {
      const content = getDisplay(res, config.country.selected);
      body.push([config.country.label, content]);
    })
    .then(() => {
      return OptionController[config.province.optionGetter]({
        category: config.province.optionCategory,
        param: config.country.selected,
      }).then(res => {
        const content = getDisplay(res, config.province.selected);
        body.push([config.province.label, content]);
      });
    })
    .then(() => {
      return OptionController[config.city.optionGetter]({
        category: config.city.optionCategory,
        param: config.province.selected,
      }).then(res => {
        const content = getDisplay(res, config.city.selected);
        body.push([config.city.label, content]);
      });
    })
    .then(() => {
      return body;
    });
}

export async function createReviewTableData(
  controls,
  callback,
  controlGetterName?
) {
  const body = [];
  const headers = ['Description', 'Content'];
  const store = new Store();
  let flat = [];
  let isQuery = false;
  let queryName = null;
  if (controls.constructor.name == 'Array') {
    flat = walkControls(controls);
  } else {
    isQuery = true;
    queryName = controlGetterName;
    const query = await controls();
    flat = walkControls(query);
  }

  store.getProfileData().then(saved => {
    /**
     * format birthdate
     * TODO - clean it later
     */
    if (saved.birthDate) {
      const computeAge = moment().diff(saved.birthDate, 'years');
      const age =
        computeAge && computeAge < 100 && computeAge > 0 ? computeAge + 1 : '';

      saved['age'] = age;
      saved.birthDate = saved.birthDate
        ? moment(saved.birthDate).format('MMMM DD, YYYY')
        : '';
    }
    /**end format birthdate */

    const origin = [];
    const normal = [];
    const files = [];

    flat.forEach(item => {
      if (item.remoteOption) {
        if (
          ['COUNTRY', 'PROVINCE', 'CITY'].includes(item.optionCategory) &&
          !['nationality'].includes(item.name)
        ) {
          origin.push(item);
        } else {
          normal.push(item);
        }
      } else if (item.tag == 'file') {
        files.push(item);
      } else if (item.name) {
        normal.push(item);
      }
    });

    // console.log(291, normal, queryName, controls);
    // console.log(files);

    const grouped: any = {};
    const mapped: any = {};
    origin.forEach(item => {
      const { name, optionCategory } = item;
      const selected = saved[name];
      grouped[optionCategory] = selected;
      mapped[optionCategory] = item;
    });

    Promise.all([
      !origin.length
        ? Promise.resolve(true)
        : getOriginValue({
            country: {
              optionCategory: mapped.COUNTRY.optionCategory,
              optionGetter: mapped.COUNTRY.optionGetter,
              selected: grouped.COUNTRY,
              label: mapped.COUNTRY.label,
            },
            province: {
              optionCategory: mapped.PROVINCE.optionCategory,
              optionGetter: mapped.PROVINCE.optionGetter,
              selected: grouped.PROVINCE,
              label: mapped.PROVINCE.label,
            },
            city: {
              optionCategory: mapped.CITY.optionCategory,
              optionGetter: mapped.CITY.optionGetter,
              selected: grouped.CITY,
              label: mapped.CITY.label,
            },
          }).then(originBody => {
            return originBody.forEach(item => {
              body.push(item);
            });
          }),

      ...normal.map((item: any) => {
        if (item.remoteOption) {
          return OptionController[item.optionGetter]({
            category: item.optionCategory,
          }).then(res => {
            const content = getDisplay(res, saved[item.name]);
            body.push([item.label, content]);
          });
        } else if (item.name) {
          let value;
          /**
           * for fatca and risk profile
           */
          if (isQuery && !value && item.options) {
            if (queryName == 'getFatcaQuestions') {
              const content = getDisplay(
                item.options,
                item.value,
                'value',
                'display'
              );

              value = content;
            } else if (queryName == 'getRiskProfileQuestions') {
              const content = getDisplay(
                item.options,
                item.value,
                'value',
                'display',
                'riskprofile'
              );
              value = content;
            }
          } else {
            value = saved[item.name];
          }

          if (value == true) {
            value = 'Yes';
          } else if (value == false) {
            value = 'No';
          }

          const resolve = Promise.resolve(body.push([item.label, value]));

          return resolve;
        }
      }),
      ...files.map(file => {
        if (!FormState.formControlValues[`blob-url-${file.name}`]) {
          FormState.formControlValues[`blob-url-${file.name}`] = signal('');
        }
        setTimeout(() => {
          getDocument(file.name);
        }, 2000);
        return Promise.resolve(
          body.push([
            file.label,
            {
              image: true,
              value: FormState.formControlValues[`blob-url-${file.name}`],
            },
          ])
        );
      }),
    ]).then(() => {
      callback({ body, headers });
    });
  });
}

export async function logged(respon) {
  const store = new Store();
  respon && respon.token && store.put('x-token', respon.token);

  if (respon.code == 0) {
    const message = respon.info;
    //error
    GlobalState.set.showModal(true, {
      text: message,
      showNegativeBtn: false,
    });
  } else if (respon.code == 99) {
    //change password
    const message =
      'Dear customer, in order to secure your account, we advise you to update you password. Sorry for the inconvenience and thank you for using Seedbox as your investment platform.';
  } else {
    const user = respon.user;
    const kyc = respon.kyc;
    const userStatus = user.userStatus;
    const email = user.email;
    // const userStatus = 'REG';
    // $rootScope.urlLogo = '#/dashboard/pera';

    if (['REG'].includes(userStatus)) {
      /**
        redirect to

        '/sign-up/activate-account/' + email
       */
      store.put('email', email);
      route('/signup/activate', true);
      return false;
    }

    store.put('lang', 'ID');
    store.put('userid', user.id);
    store.put('email', email);
    store.put('token', user.token);
    store.put('pin', user.pin || null);
    store.put('custid', kyc.id || null);
    store.put('custname', kyc.firstName || null);
    store.put('last_login', user.lastLogin || null);
    store.put('totalCart', 0 || null);
    store.put('status', userStatus);
    store.put('viewonly', kyc.viewOnly || false);

    if (['ACT'].includes(userStatus)) {
      const respon = await completenessPercentage();

      if (respon.kyc < 100) {
        // console.log(postLoginKYC);
        GlobalState.set.showModal(true, {
          text: postLoginKYC,
          showNegativeBtn: false,
          positiveCallback: () => {
            route('/kyc', true);
          },
        });
      } else if (respon.fatca < 100) {
        GlobalState.set.showModal(true, {
          text: postLoginFATCA,
          showNegativeBtn: false,
          positiveCallback: () => {
            route('/fatca', true);
          },
        });
      } else if (respon.riskProfile < 100) {
        GlobalState.set.showModal(true, {
          text: postLoginRiskProfile,
          showNegativeBtn: false,
          positiveCallback: () => {
            route('/riskprofile', true);
          },
        });
      } else {
        GlobalState.set.showModal(true, {
          text: postLoginRiskProfile,
          showNegativeBtn: false,
          positiveCallback: () => {
            route('/review', true);
          },
        });
      }
    } else {
      /**
        redirect to dashboard
        '/dashboard/pera'
       */
      // alert('/dastboard/pera');
      route('/redirect/dashboard', true);
    }
  }
}

export function blobToUrl(file) {
  const url = URL.createObjectURL(file);
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 600000);

  return url;
}

export function profilePictureUrl(key) {
  const store = new Store();
  const url = `${BASE_URL}/api/attachFile/download?id=${key}&token=${store.get(
    'token'
  )}`;
  return url;
}

export function loadImage(url) {
  return new Promise((res, rej) => {
    const image = new Image();
    image.src = url;
    image.onload = e => {
      res('valid image');
    };
    image.onerror = () => {
      rej('invalid image');
    };
  });
}

export function isFalsy(val, len) {
  if (!val) {
    return true;
  }
  const isNumber = val.constructor.name == 'Number';
  const isString = val.constructor.name == 'String';

  if (isNumber) {
    return !val;
  } else if (isString) {
    return val == 'undefined' || val == 'null' || val.length <= len;
  }
  return false;
}

export function showSpinner() {
  GlobalState.showSpinner.value = true;
}

export function hideSpinner(timeout?) {
  if (timeout) {
    return setTimeout(() => {
      GlobalState.showSpinner.value = false;
    }, timeout);
  }
  GlobalState.showSpinner.value = false;
}

export function createUrl(str, obj) {
  const s = new URLSearchParams();
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      s.append(key, obj[key]);
    }
  }

  return `${str}?${s.toString()}`;
}
